import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar, { type FilterState } from "@/components/feature/FilterBar";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import {
  CATEGORY_META,
  filterMovies,
  getMoviesByType,
  getTopicMovies,
} from "@/lib/catalog";

const DEFAULT_FILTER: FilterState = {
  genres: [],
  year: "all",
  minRating: 0,
  sort: "latest",
};

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type = "" } = useParams();
  const meta = CATEGORY_META[type];

  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setFilter(DEFAULT_FILTER);
    setVisibleCount(PAGE_SIZE);
  }, [type]);

  const baseList = useMemo(() => {
    if (type === "movie") return getMoviesByType("movie");
    if (type === "tv") return getMoviesByType("tv");
    if (type === "topic") return getTopicMovies();
    return [];
  }, [type]);

  const results = useMemo(
    () => filterMovies(baseList, filter),
    [baseList, filter]
  );

  const visible = results.slice(0, visibleCount);

  useSeo({
    title: meta ? `${meta.title} - 年轻的母亲在线` : "分类 - 年轻的母亲在线",
    description: meta
      ? `${meta.description} 当前分类共有 ${results.length} 部影视资源，支持按类型、年份、评分筛选与排序。`
      : "年轻的母亲在线影视分类频道。",
    keywords: meta
      ? `${meta.title},年轻的母亲在线,影视资源,${meta.title}列表,在线观看`
      : "年轻的母亲在线,影视分类",
  });

  if (!meta) {
    return (
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 py-12">
        <EmptyState
          title="抱歉，您访问的分类不存在"
          description="该分类可能已调整或暂无内容，试试前往电影频道继续浏览。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "分类", to: "/category/movie" },
          { label: meta.title },
        ]}
      />

      <header className="mt-5 mb-6">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          {meta.title}
        </h1>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-3xl">
          {meta.description}
        </p>
      </header>

      <div className="mb-6">
        <FilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setVisibleCount(PAGE_SIZE);
          }}
          onReset={() => {
            setFilter(DEFAULT_FILTER);
            setVisibleCount(PAGE_SIZE);
          }}
          resultCount={results.length}
        />
      </div>

      {results.length === 0 ? (
        <EmptyState
          title="没有找到符合条件的影视"
          description="当前筛选条件下暂无可展示的内容，请调整或重置上方筛选条件后重试。"
        />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6">
            {visible.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>

          {visibleCount < results.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="px-6 h-11 inline-flex items-center gap-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                加载更多
                <i className="ri-arrow-down-line"></i>
              </button>
            </div>
          )}

          <p className="mt-6 text-center text-xs text-foreground-400">
            已展示 {visible.length} / {results.length} 部影视
          </p>
        </>
      )}
    </div>
  );
}